import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MoreHorizontal, Plus, RefreshCw, UserMinus, UserPlus, X } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { useConfirm } from "@/components/layout/confirm-provider";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DashboardTabs, useRoster } from "@/features/dashboard";
import { InviteDialog } from "@/features/invitations";
import {
  type InvitationPublic,
  invitationsCreateMutation,
  invitationsListOptions,
  invitationsListQueryKey,
  invitationsRevokeMutation,
  type RosterMember,
  type UserPublic,
  usersListOptions,
  usersListQueryKey,
  usersUpdateMutation,
} from "@/lib/api";
import { useSessionStore } from "@/lib/auth";

/**
 * The team, in two parts: people who have joined, and invitations still out.
 *
 * Removing someone deactivates rather than deletes. Employees own private
 * content (their moments, drafts, and voice profile), so deleting the account
 * would take that with it. Deactivating blocks sign-in and is reversible.
 */
export const Route = createFileRoute("/_authenticated/org/employees")({
  component: EmployeesPage,
});

function EmployeesPage() {
  const members = useQuery(usersListOptions({ query: { limit: 200 } }));
  const invitations = useQuery(invitationsListOptions());
  const roster = useRoster();
  const [inviting, setInviting] = useState(false);
  const byUser = new Map(
    (roster.data ?? []).filter((r) => r.userId).map((r) => [r.userId as string, r]),
  );

  const pending = (invitations.data ?? []).filter((i) => i.status === "pending");

  return (
    <>
      <PageHeader
        eyebrow="Your team"
        title="Dashboard"
        description="The people posting on their own profiles. Invite them and they choose their own password."
        actions={
          <Button onClick={() => setInviting(true)}>
            <Plus /> Invite a teammate
          </Button>
        }
      />

      <DashboardTabs />

      <div className="space-y-8">
        <section>
          <h2 className="mb-3 font-semibold text-foreground text-lg">On the team</h2>
          {members.isPending ? (
            <Skeleton className="h-48 rounded-lg" />
          ) : members.isError ? (
            <ErrorState onRetry={() => void members.refetch()} />
          ) : members.data.items.length === 0 ? (
            <EmptyState
              title="Nobody has joined yet"
              description="Once someone accepts their invitation they will appear here."
            />
          ) : (
            <MemberTable members={members.data.items} roster={byUser} />
          )}
        </section>

        <section>
          <h2 className="mb-3 font-semibold text-foreground text-lg">
            Invitations out{pending.length > 0 ? ` (${pending.length})` : ""}
          </h2>
          {invitations.isPending ? (
            <Skeleton className="h-32 rounded-lg" />
          ) : pending.length === 0 ? (
            <p className="rounded-lg border border-border border-dashed bg-card/60 px-4 py-6 text-center text-muted-foreground text-sm">
              No invitations outstanding. Everyone you have invited has joined.
            </p>
          ) : (
            <InvitationTable invitations={pending} />
          )}
        </section>
      </div>

      <InviteDialog open={inviting} onOpenChange={setInviting} />
    </>
  );
}

/**
 * What the SMM may know about each person: their state, whether their voice
 * profile is finished, and how many posts they reported this month. Never
 * anything they wrote.
 */
function MemberTable({
  members,
  roster,
}: {
  members: UserPublic[];
  roster: Map<string, RosterMember>;
}) {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const me = useSessionStore((s) => s.user);

  const update = useMutation({
    ...usersUpdateMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersListQueryKey() }),
  });

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Voice profile</TableHead>
            <TableHead className="text-right">Posts this month</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => {
            const isMe = member.id === me?.id;
            const isActive = member.status === "active";
            return (
              <TableRow key={member.id}>
                <TableCell className="font-medium text-foreground">
                  {member.name}
                  {isMe ? <span className="ml-2 text-muted-foreground text-xs">(you)</span> : null}
                  {member.title ? (
                    <span className="block text-muted-foreground text-xs">{member.title}</span>
                  ) : null}
                </TableCell>
                <TableCell className="text-muted-foreground">{member.email}</TableCell>
                <TableCell>
                  <Badge variant={member.role === "smm" ? "solid" : "default"}>
                    {member.role === "smm" ? "Manager" : "Employee"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {isActive ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="outline">No access</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {roster.get(member.id)?.voiceComplete ? (
                    <span className="text-foreground text-sm">Done</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">Not yet</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {roster.get(member.id)?.postsThisMonth ?? 0}
                </TableCell>
                <TableCell>
                  {isMe ? null : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Actions for ${member.name}`}
                        >
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isActive ? (
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={async () => {
                              const ok = await confirm({
                                title: `Remove ${member.name}'s access?`,
                                description:
                                  "They will not be able to sign in. Their moments, drafts, and voice profile are kept, and you can restore access at any time.",
                                confirmLabel: "Remove access",
                                destructive: true,
                              });
                              if (ok) {
                                update.mutate({
                                  path: { user_id: member.id },
                                  body: { status: "inactive" },
                                });
                              }
                            }}
                          >
                            <UserMinus /> Remove access
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onSelect={() =>
                              update.mutate({
                                path: { user_id: member.id },
                                body: { status: "active" },
                              })
                            }
                          >
                            <UserPlus /> Restore access
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <p className="border-border border-t px-4 py-2.5 text-muted-foreground text-xs">
        Removing access stops someone signing in. Their moments, drafts, and voice profile are kept
        and come back if you restore them.
      </p>
    </div>
  );
}

function InvitationTable({ invitations }: { invitations: InvitationPublic[] }) {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: invitationsListQueryKey() });

  const resend = useMutation({ ...invitationsCreateMutation(), onSuccess: refresh });
  const revoke = useMutation({ ...invitationsRevokeMutation(), onSuccess: refresh });
  const busy = resend.isPending || revoke.isPending;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Invited</TableHead>
            <TableHead className="w-52" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invitations.map((invitation) => (
            <TableRow key={invitation.id}>
              <TableCell className="font-medium text-foreground">{invitation.name}</TableCell>
              <TableCell className="text-muted-foreground">{invitation.email}</TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(invitation.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      resend.mutate({
                        body: {
                          email: invitation.email,
                          name: invitation.name,
                          role: invitation.role,
                        },
                      })
                    }
                  >
                    <RefreshCw /> Resend
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    aria-label={`Cancel invitation for ${invitation.name}`}
                    className="text-muted-foreground hover:text-destructive-strong"
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Cancel the invitation to ${invitation.name}?`,
                        description: `Their link stops working immediately. You can invite ${invitation.email} again whenever you like.`,
                        confirmLabel: "Cancel invitation",
                        cancelLabel: "Keep it",
                        destructive: true,
                      });
                      if (ok) revoke.mutate({ path: { invitation_id: invitation.id } });
                    }}
                  >
                    <X /> Cancel
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="border-border border-t px-4 py-2.5 text-muted-foreground text-xs">
        Resending replaces the previous link, so only the newest one works.
      </p>
    </div>
  );
}
